import { Card } from "@/types/v2";
import { ReactNode, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { cn } from "@/lib/utils";
import {
  CardTagsForm,
  CardTagsFormProps,
} from "@/components/Dashboard/components/Card/CardTagsForm";
import { ClassNameValue } from "tailwind-merge";

type CardTagsDialogProps = CardTagsFormProps & {
  cardData: Card;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonClasses?: ClassNameValue;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const CardTagsDialog = ({
  cardData,
  buttonClasses,
  buttonIcon,
  buttonTitle,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
  onSubmit,
  ...props
}: CardTagsDialogProps) => {
  const [isOpen, setIsOpen] = useState(open ?? false);

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  const handleSave = async (values: { tags: string[] }) => {
    return onSubmit(values).finally(() => handleOpenChange(false));
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };
  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger ? (
        // Use custom trigger if provided
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Default trigger button
        !externalTrigger && (
          <DialogTrigger asChild>
            <Button
              className={cn(buttonClasses)}
              variant={"ghost-primary"}
              onClick={() => handleOpenChange(true)}
            >
              {buttonIcon}
              {buttonTitle ?? "Create Card"}
            </Button>
          </DialogTrigger>
        )
      )}

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Card Tags</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Add or remove tags from this card.
        </DialogDescription>
        <div className="flex flex-col gap-2">
          <CardTagsForm
            cancelButton={
              <Button
                type="button"
                variant={"secondary"}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            }
            submitButton={<Button type="submit">Save</Button>}
            cardData={cardData}
            onSubmit={handleSave}
            {...props}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
